/*
 * Copyright (c) 2024-2025 Nodals AI Ltd
 * All rights reserved.
 */

/**
 * Captures the initial URL, recovering UTM params from history.state if available.
 * Some third-party scripts wipe UTM params via History API but store them in history.state.utmTags
 */
export function captureInitialUrl(): string {
  const utmTags = window.history?.state?.utmTags;
  if (utmTags && typeof utmTags === 'object' && Object.keys(utmTags).length > 0) {
    const url = new URL(window.location.href);
    for (const [key, value] of Object.entries(utmTags)) {
      if (typeof value === 'string') {
        const paramKey = key.startsWith('utm_') ? key : `utm_${key}`;
        url.searchParams.set(paramKey, value);
      }
    }
    return url.toString();
  }
  return window.location.href;
}

/**
 * Returns the set of `utm_*` param names present in a URL's query string.
 */
function utmParamNames(rawUrl: string): Set<string> {
  const names = new Set<string>();
  try {
    const params = new URL(rawUrl).searchParams;
    for (const key of params.keys()) {
      if (key.startsWith('utm_')) {
        names.add(key);
      }
    }
  } catch {
    // Not a parseable URL - treat as having no utm params.
  }
  return names;
}

/**
 * Re-runs UTM recovery and upgrades `$nodals.initialUrl` if the freshly captured URL
 * contains `utm_*` params the stored value lacks.
 *
 * Some sites strip UTM params from the document URL via the History API *after* our
 * loader captured the initial URL, then populate `history.state.utmTags` later. By
 * the time a delayed conversion (dwell/engagement) fires, that state is usually
 * present, so re-capturing recovers UTMs the load-time capture missed.
 *
 * Upgrade-only: never replaces a UTM-bearing stored value with one that has fewer
 * UTMs (guards the case where the load-time URL had UTMs but history.state is empty
 * now). Returns the URL that should be used as the page reference.
 */
export function refreshInitialUrl(): string {
  const nodals = (window.$nodals = window.$nodals || {});
  const stored = nodals.initialUrl;

  const recaptured = captureInitialUrl();

  if (!stored) {
    nodals.initialUrl = recaptured;
    return recaptured;
  }

  const storedUtms = utmParamNames(stored);
  const recapturedUtms = utmParamNames(recaptured);

  // Upgrade only when the recaptured URL is a strict superset of the stored UTMs:
  // it must add at least one new utm param AND not drop any the stored value has.
  // This recovers UTMs the load-time capture missed without ever downgrading a
  // value that already carried UTMs.
  let addsNew = false;
  for (const name of recapturedUtms) {
    if (!storedUtms.has(name)) {
      addsNew = true;
      break;
    }
  }
  let dropsExisting = false;
  for (const name of storedUtms) {
    if (!recapturedUtms.has(name)) {
      dropsExisting = true;
      break;
    }
  }

  if (addsNew && !dropsExisting) {
    nodals.initialUrl = recaptured;
    return recaptured;
  }

  return stored;
}
